import React, { useState, useEffect } from 'react'
import Card from '../../components/Card.jsx'
import * as api from '../../services/api.js'
import { todayISO, dayIndex } from '../../utils/dates.js'

export const DAILY_MISSIONS = [
  { emoji: '🔴', text: 'Find something red in nature — a berry, a petal, some autumn bark.' },
  { emoji: '🌿', text: 'Spot three different shades of green and really notice the difference between them.' },
  { emoji: '📸', text: 'Take a photo looking straight up through tree branches or overhead wires.' },
  { emoji: '🪨', text: 'Find a pattern in bark, stone, or pavement that genuinely catches your eye.' },
  { emoji: '🐦', text: 'Spot a bird and notice its exact colours before it flies away.' },
  { emoji: '🌊', text: 'Find water catching the light — a puddle, a stream, dew on a leaf.' },
  { emoji: '👁️', text: 'Find something tiny you would normally walk straight past without noticing.' },
  { emoji: '🌸', text: 'Find something with an interesting smell — earth, flowers, rain, pine resin.' },
  { emoji: '👂', text: 'Pause for 30 seconds. Notice a sound you have never paid attention to before.' },
  { emoji: '🌤️', text: 'Find three things that are a similar colour to the sky right now.' },
  { emoji: '🪺', text: 'Spot something a bird has made, carried, or left behind.' },
  { emoji: '🍂', text: 'Find a leaf with an interesting shape, colour, or marking on it.' },
  { emoji: '💧', text: 'Find something touched by dew, frost, or rain.' },
  { emoji: '🐜', text: 'Spot an insect going about its day — really watch it for a full moment.' },
  { emoji: '✨', text: 'Find something beautiful that most people would walk straight past.' },
  { emoji: '📷', text: 'Crouch down and take a photo from ground level, looking upward.' },
  { emoji: '🔬', text: 'Find something that makes you want to look more closely.' },
  { emoji: '🌈', text: 'Spot something a colour you rarely see in nature.' },
  { emoji: '🤲', text: 'Find a texture worth running your fingers over — rough, smooth, surprisingly bumpy.' },
  { emoji: '☀️', text: 'Notice how the light falls on something completely ordinary right now.' },
  { emoji: '🧚', text: 'Find something that looks like it belongs in a fairy tale.' },
  { emoji: '🌺', text: 'Spot three living things sharing exactly the same small space.' },
  { emoji: '🏚️', text: 'Find something that has clearly been here for a very long time.' },
  { emoji: '⭕', text: 'Find something perfectly round in nature — not made by human hands.' },
  { emoji: '🌙', text: 'Notice the shadows around you. Find one with an interesting, unexpected shape.' },
  { emoji: '🦋', text: 'Spot an animal doing something that makes you stop and smile.' },
  { emoji: '🌾', text: 'Find something growing in a place you would not expect it to grow.' },
  { emoji: '🔵', text: 'Find three different blues — sky, water, a door, a flower, a reflection.' },
  { emoji: '🏔️', text: 'Find something that looks different up close than it does from far away.' },
  { emoji: '🌻', text: 'Find something that is clearly facing toward the light.' },
]

const BONUS_MISSIONS = [
  { emoji: '⏱️', text: 'Stand still for 60 seconds and count every distinct sound you can hear.' },
  { emoji: '🔤', text: 'Find something outside that starts with the same letter as your first name.' },
  { emoji: '☁️', text: 'Spend 30 seconds watching a single cloud move across the sky.' },
  { emoji: '🪴', text: 'Find something living in a completely unlikely or unexpected place.' },
  { emoji: '🔘', text: 'Spot something near you that is perfectly symmetrical.' },
  { emoji: '🤔', text: 'Notice five things on your route that you have genuinely never paid attention to before.' },
  { emoji: '💎', text: 'Find a stone worth keeping — shape, colour, or feel that speaks to you.' },
  { emoji: '🎨', text: 'Find something that changes colour depending on how the light hits it.' },
]

function calcStreak(dates) {
  if (!dates || !dates.length) return 0
  const unique = [...new Set(dates)].sort().reverse()
  const today = new Date().toISOString().slice(0, 10)
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
  if (unique[0] !== today && unique[0] !== yesterday) return 0
  let streak = 1
  for (let i = 1; i < unique.length; i++) {
    const diff = Math.round(
      (new Date(unique[i - 1]) - new Date(unique[i])) / 86400000
    )
    if (diff === 1) streak++
    else break
  }
  return streak
}

const DEFAULT_MISSION = { completions: [], bonusCompleted: {} }

export default function WalkMission() {
  const [data, setData] = useState(DEFAULT_MISSION)
  useEffect(() => { api.getWalkState('walk-mission').then(d => setData({ ...DEFAULT_MISSION, ...(d || {}) })) }, [])

  const today = todayISO()
  const idx = dayIndex()
  const mission = DAILY_MISSIONS[idx % DAILY_MISSIONS.length]
  const bonuses = [
    BONUS_MISSIONS[idx % BONUS_MISSIONS.length],
    BONUS_MISSIONS[(idx + 3) % BONUS_MISSIONS.length],
  ]
  const isDone = data.completions.includes(today)
  const bonusDone = data.bonusCompleted[today] || []
  const streak = calcStreak(data.completions)

  function updateData(updater) {
    setData(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      api.saveWalkState('walk-mission', next)
      return next
    })
  }

  function completeMission() {
    if (isDone) return
    updateData(prev => ({ ...prev, completions: [...prev.completions, today].slice(-180) }))
  }

  function toggleBonus(i) {
    updateData(prev => {
      const existing = prev.bonusCompleted[today] || []
      const updated = existing.includes(i) ? existing.filter(x => x !== i) : [...existing, i]
      return { ...prev, bonusCompleted: { ...prev.bonusCompleted, [today]: updated } }
    })
  }

  return (
    <Card label="Check-in · Activities" title="Today's Walk Mission">

      {/* Streak stats */}
      <div className="mission-stat-row">
        <div className="mission-stat-box">
          <div className="mission-stat-value">{streak}</div>
          <div className="mission-stat-label">day streak</div>
        </div>
        <div className="mission-stat-box">
          <div className="mission-stat-value">{data.completions.length}</div>
          <div className="mission-stat-label">total missions</div>
        </div>
        <div className="mission-stat-box">
          <div className="mission-stat-value">
            {Object.values(data.bonusCompleted).reduce((n, arr) => n + arr.length, 0)}
          </div>
          <div className="mission-stat-label">bonus done</div>
        </div>
      </div>

      {/* Daily mission card */}
      <div style={{
        background: isDone ? 'var(--accent)' : 'var(--surf)',
        borderRadius: '12px',
        padding: '1.25rem 1.4rem',
        marginBottom: isDone ? '1.25rem' : '0.75rem',
        transition: 'background 0.35s',
      }}>
        <p style={{
          fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.12em',
          color: isDone ? 'rgba(255,255,255,0.7)' : 'var(--stone)',
          marginBottom: '0.5rem',
        }}>
          {mission.emoji} Today's Mission
        </p>
        <p style={{
          fontSize: '1.05rem', lineHeight: '1.65',
          color: isDone ? 'white' : 'var(--ink)',
          margin: 0,
        }}>
          {mission.text}
        </p>
        {isDone && (
          <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)', marginTop: '0.6rem', marginBottom: 0 }}>
            ✓ Completed today — great work
          </p>
        )}
      </div>

      {!isDone && (
        <button className="save-btn ready" onClick={completeMission} style={{ marginBottom: '1.5rem' }}>
          ✓ Mission Complete
        </button>
      )}

      {/* Bonus missions */}
      <p style={{
        fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.12em',
        color: 'var(--stone)', marginBottom: '0.6rem',
      }}>
        ⭐ Bonus Missions
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {bonuses.map((b, i) => {
          const done = bonusDone.includes(i)
          return (
            <div
              key={i}
              className="bonus-row"
              style={{
                background: done ? 'var(--surf)' : 'transparent',
                borderColor: done ? 'var(--accent)' : 'rgba(0,0,0,0.1)',
              }}
              onClick={() => toggleBonus(i)}
            >
              <span
                className="bonus-check"
                style={{
                  background: done ? 'var(--accent)' : 'transparent',
                  borderColor: done ? 'var(--accent)' : 'rgba(0,0,0,0.25)',
                }}
              >
                {done ? '✓' : ''}
              </span>
              <span style={{
                fontSize: '0.88rem', lineHeight: '1.5',
                color: done ? 'var(--bark)' : 'var(--ink)',
                textDecoration: done ? 'line-through' : 'none',
                opacity: done ? 0.65 : 1,
              }}>
                {b.emoji} {b.text}
              </span>
            </div>
          )
        })}
      </div>

    </Card>
  )
}
